<template>
  <el-dialog
    title="Upload New Document"
    :visible="visible"
    size="small"
    :close-on-click-modal="false"
    :show-close="false"
    class="proscan-upload-dialog"
    @close="close">
    <p class="dialog-subtitle">Files are processed by the AI engine and land as drafts in ProBooks.</p>
    <div class="upload-notes">
      <p class="upload-notes-title"><i class="el-icon-information"></i> How uploads work</p>
      <ul>
        <li>Upload one document per file for best results</li>
        <li>Invoices and POs work best at ~300 line items or fewer</li>
        <li>Vendors are matched for you; a new one is added only if there's no match</li>
        <li>Existing vendor details stay as they are — edit the vendor to change them</li>
      </ul>
    </div>
    <table class="upload-fields" style="margin-top: 20px !important;">
      <tr>
        <td class="field-label"><span class="required">*</span>Document Type</td>
        <td> 
          <el-select v-model="documentType" placeholder="Document Type" style="width: 100%;">
            <el-option
              v-for="option in documentTypes"
              :key="option.value"
              :label="option.label"
              :value="option.value" :class="option.divided ? 'el-option--divided' : ''" />
          </el-select>
        </td>
      </tr>
      <tr>
        <td class="field-label"><span class="required">*</span>Batch Name</td>
        <td>
          <div class="batch-field">
            <el-select
              v-model="batch"
              filterable
              remote
              clearable
              placeholder="Search batches..."
              :remote-method="searchBatches"
              :loading="searchingBatches"
              style="width: 100%;">
              <el-option
                v-for="option in batchOptions"
                :key="option.id"
                :label="option.name"
                :value="option.id" />
            </el-select>
            <el-button
              v-if="hasCreateBatchPermission"
              class="add-batch-btn"
              icon="plus"
              size="small"
              @click="openCreateBatchModal" />
          </div>
        </td>
      </tr>
      <tr>
        <td class="field-label">Company</td>
        <td class="field-value">{{ selectedBatchInfo.company || '—' }}</td>
      </tr>
      <tr>
        <td class="field-label">Production</td>
        <td class="field-value">{{ selectedBatchInfo.production || '—' }}</td>
      </tr>
      <tr>
        <td class="field-label">Bank</td>
        <td class="field-value">{{ selectedBatchInfo.bank || '—' }}</td>
      </tr>
      <tr>
        <td class="field-label">Currency</td>
        <td class="field-value">{{ selectedBatchInfo.currency || '—' }}</td>
      </tr>
    </table>

    <div
      class="dropzone"
      :class="{ 'dropzone-active': draggingOver }"
      @dragover.prevent="draggingOver = true"
      @dragleave.prevent="draggingOver = false"
      @drop.prevent="handleDrop">
      <i class="el-icon-upload dropzone-icon"></i>
      <p class="dropzone-text">Drop files here to upload</p>
      or <a href="javascript:void(0)" style="text-decoration: underline; color: #409EFF;" @click="$refs.fileInput.click()" >browse</a>
      <p class="dropzone-hint">PDF, PNG, JPG, JPEG, WEBP · up to {{ maxFiles }} files, {{ maxFileSizeMb }} MB each</p>
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden-file-input"
        :accept="acceptedFiles"
        @change="handleFilesSelected" />
    </div>

    <div v-if="selectedFiles.length" class="selected-files">
      <p class="selected-files-title">{{ selectedFiles.length }} FILE{{ selectedFiles.length === 1 ? '' : 'S' }} SELECTED</p>
      <div v-for="(file, i) in selectedFiles" :key="i" class="selected-file">
        <span class="selected-file-name"><i class="el-icon-document"></i> {{ file.name }}</span>
        <span class="selected-file-size">{{ readableSize(file.size) }}</span>
        <i class="el-icon-close remove-btn" @click="removeFile(i)"></i>
      </div>
    </div>

    <div v-if="uploadError" class="upload-error">{{ uploadError }}</div>

    <span slot="footer">
      <el-button @click="close" :disabled="uploading">Cancel</el-button>
      <el-button type="primary" :loading="uploading" :disabled="!canUpload" @click="upload">
        Upload{{ selectedFiles.length ? ` ${selectedFiles.length} File${selectedFiles.length === 1 ? '' : 's'}` : '' }}
      </el-button>
    </span>

  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'

  const MAX_FILES = 100
  const MAX_FILE_SIZE_MB = 20
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024

  export default {
    name: 'ProscanUploadDialog',
    props: {
      visible: Boolean,
      // Batch created through the New Batch modal the parent renders, selected on arrival
      createdBatchId: String
    },
    data () {
      return {
        documentType: 'invoice',
        batch: '',
        selectedFiles: [],
        batchOptions: [],
        searchingBatches: false,
        draggingOver: false,
        uploading: false,
        uploadError: null,
        acceptedFiles: '.pdf,.png,.jpg,.jpeg,.webp',
        acceptedExtensions: ['pdf', 'png', 'jpg', 'jpeg', 'webp'],
        maxFiles: MAX_FILES,
        maxFileSizeMb: MAX_FILE_SIZE_MB,
        documentTypes: []
      }
    },
    computed: {
      ...mapGetters({
        getBatches: 'getBatches',
        getBatchById: 'getBatchById',
        getCompanyById: 'getCompanyById',
        getProductionById: 'getProductionById',
        getBankById: 'getBankById',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      hasVendorCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['system.view_vendor', 'system.add_vendor'])
      },
      hasPOCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_purchaseorder', 'entry.add_purchaseorder'])
      },
      hasAPCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_accountspayable', 'entry.add_accountspayable'])
      },
      hasCreateBatchPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_batch', 'entry.add_batch'])
      },
      canUpload () {
        return !this.uploading && !!this.documentType && !!this.batch && !!this.selectedFiles.length
      },
      /**
       * The read-only context shown under the batch picker, resolved from the selected batch
       * @return {{company: string, production: string, bank: string, currency: string}}
       */
      selectedBatchInfo () {
        const batch = this.batch ? this.getBatchById(this.batch) : null
        if (!batch || !batch.id) return { company: '', production: '', bank: '', currency: '' }
        const company = batch.company ? this.getCompanyById(batch.company) : null
        const production = batch.production ? this.getProductionById(batch.production) : null
        const bank = batch.bank ? this.getBankById(batch.bank) : null
        const currency = this.getCurrencyByBatchId(batch.id)
        return {
          company: (company && company.name) || '',
          production: (production && production.name) || '',
          bank: (bank && bank.name) || '',
          currency: (currency && (currency.code || currency.name)) || ''
        }
      }
    },
    methods: {
      /**
       * Remote search for the batch picker
       * @param query
       * @return {Promise<void>}
       */
      async searchBatches (query) {
        if (!query) {
          this.batchOptions = []
          return
        }
        this.searchingBatches = true
        try {
          await this.$store.dispatch('fetchBatchesWithQuery', { search: query })
          this.batchOptions = this.getBatches.filter(b => {
            return b.name && b.name.toLowerCase().includes(query.toLowerCase())
          })
        } catch (e) {
          console.log(e)
        } finally {
          this.searchingBatches = false
        }
      },
      openCreateBatchModal () {
        this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: true, inDataEntryView: true })
      },
      /**
       * Selects a batch that was just created through the New Batch modal
       * @param batchId
       */
      selectCreatedBatch (batchId) {
        if (!batchId) return
        const model = this.getBatchById(batchId)
        if (!this.batchOptions.some(option => option.id === batchId)) {
          this.batchOptions = [{ id: batchId, name: model.name || '' }, ...this.batchOptions]
        }
        this.batch = batchId
      },
      handleFilesSelected (event) {
        this.addFiles(Array.from(event.target.files))
        event.target.value = ''      // Allows re-selecting the same file after removing it
      },
      handleDrop (event) {
        this.draggingOver = false
        this.addFiles(Array.from(event.dataTransfer.files))
      },
      /**
       * Adds the files that meet the upload rules and says why any of the others were left out
       * @param files
       */
      addFiles (files) {
        const rejected = []
        const accepted = files.filter(file => {
          const extension = file.name.split('.').length > 1 ? file.name.split('.').pop().toLowerCase() : ''
          if (!this.acceptedExtensions.includes(extension)) {
            rejected.push(`${file.name} is not an accepted format`)
            return false
          }
          if (file.size > MAX_FILE_SIZE) {
            rejected.push(`${file.name} is larger than ${MAX_FILE_SIZE_MB} MB`)
            return false
          }
          return true
        })
        const room = MAX_FILES - this.selectedFiles.length
        if (accepted.length > room) {
          rejected.push(`Only ${MAX_FILES} files can be uploaded at a time`)
          accepted.length = Math.max(room, 0)
        }
        this.selectedFiles = [...this.selectedFiles, ...accepted]
        this.uploadError = rejected.length ? rejected.join(' · ') : null
      },
      removeFile (index) {
        this.selectedFiles.splice(index, 1)
      },
      readableSize (bytes) {
        if (!bytes) return ''
        return bytes >= 1048576
          ? `${(bytes / 1048576).toFixed(1)} MB`
          : `${Math.max(1, Math.round(bytes / 1024))} KB`
      },
      /**
       * Sends the selected documents for extraction and hands the created log rows back to
       * the parent, which owns the results list and its polling
       * @return {Promise<void>}
       */
      async upload () {
        this.uploading = true
        this.uploadError = null
        try {
          const rows = await this.$store.dispatch('uploadProscanDocuments', {
            documentType: this.documentType,
            batch: this.batch,
            files: this.selectedFiles
          })
          this.$emit('uploaded', rows)
          this.reset()
          this.$emit('update:visible', false)
        } catch (e) {
          this.uploadError = this.formatUploadError(e)
        } finally {
          this.uploading = false
        }
      },
      /**
       * Turns the API's field-keyed 400 body into a readable message
       * @param e
       * @return {string}
       */
      formatUploadError (e) {
        const body = e && e.response && e.response.data
        if (!body) return 'Unable to upload the selected documents.'
        if (typeof body === 'string') return body
        return Object.keys(body)
          .map(field => `${field}: ${[].concat(body[field]).join(' ')}`)
          .join(' | ')
      },
      reset () {
        this.selectedFiles = []
        this.uploadError = null
        this.draggingOver = false
      },
      close () {
        this.reset()
        this.$emit('update:visible', false)
      }
    },
    watch: {
      createdBatchId (id) {
        this.selectCreatedBatch(id)
      }
    },
    async mounted () {
      // Preload a first page of batches so the picker isn't empty before the user types
      await this.$store.dispatch('fetchBatchesWithQuery', { search: ' ' })
      this.batchOptions = this.getBatches.filter(b => b.name) || []
      if (this.hasAPCreatePermission) {
        this.documentTypes.push({ label: 'AP Invoice', value: 'invoice' })
      }
      if (this.hasPOCreatePermission) {
        this.documentTypes.push({ label: 'Purchase Order', value: 'purchase_order' })
      }
      if (this.hasVendorCreatePermission) {
        this.documentTypes.push({ label: 'Vendor', value: 'vendor_documents', divided: true })
      }
    }
  }
</script>
<style>
  .proscan-upload-dialog .el-dialog--small {
    width: 45% !important;
  }
  .proscan-upload-dialog .el-dialog__body {
    padding: 2px 20px !important;
  }
  .el-option--divided {
    border-top: 1px solid #dcdfe6 !important; /* Changes the line color and thickness */
    margin-top: 1px !important; /* Adds some space above the line */
  }
  .el-dialog__header span {
    font-size: 22px;
    font-weight: bold;
  }

</style>
<style scoped>
  .dialog-subtitle {
    font-size: 13px;
    color: #8492a6;
    text-align: left;
    padding-bottom: 9px !important;
    margin-bottom: 22px !important;
    border-bottom: 1px solid #d5d2d2;
  }
  .upload-fields {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
  }
  .upload-fields td {
    border: 1px solid #dfe6ec;
    padding: 6px 10px;
    font-size: 13px;
  }
  .field-label {
    width: 35%;
    text-align: right;
    color: #475669;
    background: #f9f9fa;
  }
  .field-value {
    text-align: left;
    color: #1f2d3d;
  }
  .required {
    color: #ff4949;
    margin-right: 2px;
  }
  .batch-field {
    display: flex;
    align-items: center;
  }
  .add-batch-btn {
    margin-left: 6px;
  }
  .dropzone {
    border: 1px dashed #c0ccda;
    border-radius: 4px;
    padding: 24px;
    text-align: center;
    background: #fbfcfd;
  }
  .dropzone-active {
    border-color: #20a0ff;
    background: #eef7ff;
  }
  .dropzone-icon {
    font-size: 28px;
    color: #8492a6;
  }
  .dropzone-text {
    margin: 8px 0 4px;
    color: #475669;
    font-size: 14px;
  }
  .dropzone-hint {
    margin: 6px 0 0;
    color: #8492a6;
    font-size: 12px;
  }
  .hidden-file-input {
    display: none;
  }
  .selected-files {
    margin-top: 16px;
    text-align: left;
  }
  .selected-files-title {
    font-size: 12px;
    color: #8492a6;
    margin: 0 0 6px;
  }
  .selected-file {
    display: flex;
    align-items: center;
    border: 1px solid #dfe6ec;
    border-radius: 3px;
    padding: 6px 10px;
    margin-bottom: 6px;
    font-size: 13px;
  }
  .selected-file-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .selected-file-size {
    color: #8492a6;
    margin: 0 10px;
  }
  .remove-btn {
    cursor: pointer;
    color: #8492a6;
  }
  .upload-notes {
    margin-bottom: 16px;
    padding: 12px 14px;
    border: 1px solid #d1e4f7;
    border-radius: 4px;
    background: #eef5fc;
    text-align: left;
  }
  .upload-notes-title {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: bold;
    color: #1f4e79;
  }
  .upload-notes ul {
    margin: 0;
    padding-left: 20px;
    font-size: 13px;
    color: #3a6ea5;
    line-height: 1.7;
  }
  .upload-error {
    margin-top: 12px;
    color: #ff4949;
    font-size: 13px;
    text-align: left;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Proscan/ProscanUploadDialog.vue